import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useCurrentAppUser } from './useCurrentAppUser';

export interface MissedCall {
  id: string;
  started_at: string | null;
  created_at: string;
  from_e164: string | null;
  to_e164: string | null;
  status: string | null;
  duration_seconds: number | null;
  handled_by_app_user_id: string | null;
  contact: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    primary_phone: string | null;
  } | null;
  hasReturnTask?: boolean;
}

const MISSED_STATUSES = ['no-answer', 'busy', 'failed', 'canceled'];

/**
 * Inbound calls that were never actually answered:
 * either Twilio reported a missed status, or the call has no talk time.
 */
export function useMissedCalls(days = 7) {
  return useQuery({
    queryKey: ['missed-calls', days],
    queryFn: async (): Promise<MissedCall[]> => {
      const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

      // Somente ligações recebidas nos números da empresa (atendimento)
      const { data: companyNumbers, error: numbersError } = await supabase
        .from('company_phone_numbers')
        .select('phone_number')
        .eq('is_active', true);

      if (numbersError) throw numbersError;

      const numbers = (companyNumbers ?? [])
        .map((n) => n.phone_number)
        .filter(Boolean) as string[];

      if (numbers.length === 0) return [];

      const { data, error } = await supabase
        .from('calls')
        .select(`
          id,
          created_at,
          started_at,
          from_e164,
          to_e164,
          status,
          duration_seconds,
          handled_by_app_user_id,
          contact:contacts!contact_calls_contact_id_fkey(id, first_name, last_name, primary_phone)
        `)
        .eq('direction', 'inbound')
        .in('to_e164', numbers)
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(300);

      if (error) throw error;


      const rows = (data ?? []) as unknown as MissedCall[];

      const missed = rows.filter((c) => {
        const noTalkTime = !c.duration_seconds || c.duration_seconds === 0;
        return MISSED_STATUSES.includes(c.status || '') || noTalkTime;
      });

      // Mark calls that already have an open return-call task for the contact
      const contactIds = Array.from(
        new Set(missed.map((c) => c.contact?.id).filter(Boolean) as string[])
      );

      if (contactIds.length > 0) {
        const { data: tasks } = await supabase
          .from('tasks')
          .select('contact_id, status, title')
          .in('contact_id', contactIds)
          .neq('status', 'completed')
          .ilike('title', 'Retornar ligação%');

        const withTask = new Set((tasks ?? []).map((t) => t.contact_id));
        missed.forEach((c) => {
          c.hasReturnTask = !!(c.contact?.id && withTask.has(c.contact.id));
        });
      }

      return missed;
    },
    staleTime: 60_000,
  });
}

export function useCreateReturnCallTask() {
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentAppUser();

  return useMutation({
    mutationFn: async ({
      call,
      assignedTo,
      attempts,
    }: { call: MissedCall; assignedTo?: string | null; attempts?: number }) => {
      const name = call.contact
        ? `${call.contact.first_name ?? ''} ${call.contact.last_name ?? ''}`.trim()
        : null;
      const phone = call.from_e164 || call.contact?.primary_phone || 'número desconhecido';

      const due = new Date();
      due.setHours(due.getHours() + 2);

      const { error } = await supabase.from('tasks').insert([
        {
          title: `Retornar ligação — ${name || phone}`,
          description: `Ligação perdida recebida em ${new Date(
            call.started_at || call.created_at
          ).toLocaleString('pt-BR')} de ${phone}.${
            attempts && attempts > 1 ? ` Total de ${attempts} tentativas no período.` : ''
          }`,
          status: 'pending',
          priority: 'high',
          due_date: due.toISOString(),
          contact_id: call.contact?.id ?? null,
          assigned_to: assignedTo ?? call.handled_by_app_user_id ?? currentUser?.id ?? null,
          created_by: currentUser?.id ?? null,
        },
      ]);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Tarefa de retorno criada');
      queryClient.invalidateQueries({ queryKey: ['missed-calls'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error: Error) => {
      toast.error('Erro ao criar tarefa: ' + error.message);
    },
  });
}
